import { Media, State } from './types';
import { DetachedDomElementError } from './errors';
import { Log } from '../../AuxillaryService/Log';
import { DynamicEventEmitter, IHasEventEmitter } from '../../Utility/EventEmitter';

export type VideoHandlerConfig = {
    continuousPlayback?: boolean;
    muteBeforeUserInteraction: boolean;
    muted?: boolean;
    startTime?: number;
};

/**
 * Classes implementing this should have a fairly simple lifecycle.
 *
 * - Create the handler class.
 * - Set the element where the player should appear.
 * - Play/Pause the video.
 * - When done, stop the video.
 * - (Optional) Play the video again; stop when done; repeat.
 */
export interface VideoHandlerInterface {
    /**
     * Current state of the player.
     */
    readonly state: State;

    /**
     * A variable to help identify the video playing.
     *
     * (For debugging and logging)
     */
    readonly id: string;

    /**
     * Set the element that with contain the player. The element should be
     * empty.
     *
     * (Will destroy existing player when changing containing element)
     *
     * @param element The containing element
     */
    setElement(element: HTMLElement): void;

    /**
     * @throws {@link ReferenceError}
     *
     * If containing element has not been set.
     *
     * @throws {@link DetachedDomElementError}
     *
     * If containing element has been detached from the DOM. Can happen if the
     * containing element is deleted/removed from the DOM.
     */
    getElement(): HTMLElement;

    /**
     * Play the video.
     *
     * (Will create player if not already created)
     *
     * @throws {@link DetachedDomElementError}
     *
     * If containing element has been detached from the DOM. Can happen if the
     * containing element is deleted/removed from the DOM.
     */
    play(): Promise<void>;

    /**
     * Pause the video.
     *
     * (Will create player if not already created)
     *
     * @throws {@link DetachedDomElementError}
     *
     * If containing element has been detached from the DOM. Can happen if the
     * containing element is deleted/removed from the DOM.
     */
    pause(): Promise<void>;

    /**
     * Stops the video and destroys the player.
     */
    stop(): Promise<void>;
}

export abstract class VideoHandlerBase<TPlayer> implements VideoHandlerInterface, IHasEventEmitter {
    protected abstract _type: string;
    protected abstract _videoId: string | number;
    protected _state: State;
    protected _element?: HTMLElement;
    protected _player?: TPlayer;
    protected _buildPromise?: Promise<TPlayer>;

    protected _continuousPlayback: boolean;
    protected _muteBeforeUserInteraction: boolean;
    protected __muted: boolean;
    protected _showControls: boolean;
    protected _startTime: number;

    eventEmitter: DynamicEventEmitter;

    constructor(config: VideoHandlerConfig) {
        this._continuousPlayback = config.continuousPlayback ?? false;
        this._muteBeforeUserInteraction = config.muteBeforeUserInteraction;
        this.__muted = config.muted || false;
        this._showControls = false;
        this._startTime = config.startTime || 0;

        this._state = State.STOPPED;

        // event listener
        this.eventEmitter = new DynamicEventEmitter();
        this.eventEmitter.addListener(Media.Ended, async () => {
            if (this._continuousPlayback) {
                Log.Debug(`[${this.id}] Loop the video endlessly.`, null, __fileline);
                await this.play();
            }
        });
    }

    protected get _muted(): boolean {
        if (this._muteBeforeUserInteraction && navigator.userActivation?.hasBeenActive !== true) {
            return true;
        }
        return this.__muted;
    }

    /**
     * @see {@link VideoHandlerInterface.state | VideoHandlerInterface.state }
     */
    get state(): State {
        return this._state;
    }

    /**
     * @see {@link VideoHandlerInterface.id | VideoHandlerInterface.id }
     */
    get id(): string {
        return `${this._type}:${this._videoId}`;
    }

    async setElement(element: HTMLElement): Promise<void> {
        const newElement = element !== this._element;
        if (this._element) {
            if (newElement) {
                Log.Debug(
                    `[${this.id}] Destroy old player since the containing element has changed.`,
                    null,
                    __fileline
                );
                await this._destroyPlayer();
            } else {
                Log.Debug(
                    `[${this.id}] Do nothing since the containing element did not change.`,
                    null,
                    __fileline
                );
            }
        }
        element.dataset.videoId = this.id;
        element.classList.add('video-container');
        element.classList.add(`video-container--${this._type}`);
        this._element = element;
        if (newElement) {
            this.eventEmitter.setEventEmitterElement(element);
        }
    }

    getElement(): HTMLElement {
        this._assertElementIsInDOM();
        return this._element;
    }

    /**
     * @throws ReferenceError
     *
     * If containing element has not been defined.
     *
     * @throws {@link DetachedDomElementError}
     *
     * If containing element has been detached from the DOM. Can happen if the
     * containing element is deleted/removed from the DOM.
     */
    protected _assertElementIsInDOM(): asserts this is { _element: HTMLElement } {
        if (!this._element) {
            throw new ReferenceError(`[${this.id}] The containing element is not defined.`);
        } else if (!document.body.contains(this._element)) {
            throw new DetachedDomElementError(
                `[${this.id}] The containing element is not in the DOM.`
            );
        }
    }

    /**
     * @throws ReferenceError
     * If player object has not been defined.
     */
    protected _assertPlayerExists(): asserts this is { _player: TPlayer } {
        if (!this._player) {
            throw new ReferenceError(`[${this.id}] The player object is not defined.`);
        }
    }

    protected abstract _createNewPlayer(): Promise<TPlayer>;

    /**
     * Build player if needed
     */
    protected async _buildPlayer(): Promise<TPlayer> {
        this._assertElementIsInDOM();
        if (!this._player) {
            if (!this._buildPromise) {
                this._buildPromise = this._createNewPlayer();
            } else {
                Log.Warning(`[${this.id}] Player build is already in progress.`, null, __fileline);
            }
            try {
                this._player = await this._buildPromise;
            } finally {
                delete this._buildPromise;
            }
        } else {
            Log.Debug(`[${this.id}] Player is already built.`, null, __fileline);
        }
        return this._player;
    }

    protected _getAttachedPlayer(): TPlayer {
        this._assertElementIsInDOM();
        this._assertPlayerExists();
        return this._player;
    }

    /**
     * (useful when needing to destroy detached player)
     */
    protected _getPossiblyDetachedPlayer(): TPlayer {
        this._assertPlayerExists();
        return this._player;
    }

    protected abstract _setCurrentTime(seconds: number): Promise<void>;

    protected abstract _doPlay(): Promise<void>;

    async play(): Promise<void> {
        await this._buildPlayer();
        if (this.state != State.PAUSED) {
            await this._setCurrentTime(this._startTime);
        }
        await this._doPlay();
        this._state = State.PLAYING;
    }

    protected abstract _doPause(): Promise<void>;

    async pause(): Promise<void> {
        await this._buildPlayer();
        await this._doPause();
        this._state = State.PAUSED;
    }

    protected abstract _doDestroyPlayer(): Promise<void>;

    protected async _destroyPlayer(): Promise<void> {
        if (!this._player) {
            return;
        }
        await this._doDestroyPlayer();
        delete this._player;

        if (this._element) {
            // remove leftover player elements
            Array.from(this._element.children).forEach((child) => {
                child.parentNode?.removeChild(child);
            });
        }
    }

    async stop(): Promise<void> {
        await this._destroyPlayer();
        this._state = State.STOPPED;
    }
}
